import i18n from "i18next";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DeliveryPackageForm } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackageForm.tsx";
import {
	DeliveryPackageToBundleSelectionView,
	DeliveryPackageToBundleSelectionViewOnCompletedParams,
	DeliveryPackageToBundleSelectionViewProps,
} from "src/components/views/erp/delivery/deliveryPackageBundle/DeliveryPackageToBundleSelectionView.tsx";
import { useAavoToast } from "src/components/common/toast/AavoToastContext.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faPalletBoxes } from "@fortawesome/pro-regular-svg-icons";
import { DeliveryPackagesDataGrid } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackagesDataGrid.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { getDeliveryPackageSources } from "src/components/views/erp/delivery/deliveryPackageSourceUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackageBundleCreationViewProps extends Omit<
	DeliveryPackageToBundleSelectionViewProps,
	"onCompleted" | "storageKey" | "extraActions"
> {
	onCompleted?: () => Promise<unknown>;
	noStorage?: boolean;
}

export const DeliveryPackageBundleCreationView = ({
	onCompleted: onCompletedOuter,
	noStorage,
	...other
}: DeliveryPackageBundleCreationViewProps) => {
	const { openDialog } = useGenericDialog();
	const { showToast } = useAavoToast();
	const { appUserId } = useGlobalInitData();

	return (
		<DeliveryPackageToBundleSelectionView
			{...other}
			storageKey={noStorage ? undefined : "57A778701F28AF2B"}
			onCompleted={onSelectionViewCompleted}
			extraActions={
				<>
					<AsyncButton
						label={i18n.t("delivery.bundles")}
						icon={faPalletBoxes}
						variant={"outlined"}
						size={"large"}
						onClick={() => {
							openDialog({
								title: i18n.t("delivery.bundles"),
								fullScreenOnMobile: true,
								agentContext: {
									key: defineAgentUiContextKey("erp.delivery.deliveryPackageBundles.dialog"),
									title: i18n.t("delivery.bundles"),
									data: {
										createdByUserId: appUserId,
									},
								},
								content: (
									<DeliveryPackagesDataGrid
										gridId={"BC3BE134E25E4411"}
										bundleHandling={"ONLY_BUNDLES"}
										createdByUserId={appUserId}
									/>
								),
							});
						}}
					/>
				</>
			}
		/>
	);

	async function onSelectionViewCompleted({
		selectedPackages,
	}: DeliveryPackageToBundleSelectionViewOnCompletedParams) {
		const deliveryPackageSources = selectedPackages.flatMap((pkg) => getDeliveryPackageSources(pkg));

		openDialog(({ closeDialog, onContentEdited }) => ({
			title: i18n.t("delivery.new_bundle"),
			size: "sm",
			fullScreenOnMobile: true,
			agentContext: {
				key: defineAgentUiContextKey("erp.delivery.deliveryPackageBundle.formDialog"),
				title: i18n.t("delivery.new_bundle"),
				data: {
					bundleChildPackageIds: selectedPackages.map((p) => p.deliveryPackageId),
				},
			},
			content: (
				<DeliveryPackageForm
					initialization={{
						type: "newPackage",
						deliveryId: undefined,
						deliveryPackageSources: deliveryPackageSources,
						bundleChildPackageIds: selectedPackages.map((p) => p.deliveryPackageId),
						weight: selectedPackages.reduce((sum, pkg) => sum + (pkg.weight ?? 0), 0),
					}}
					onFormEdited={onContentEdited}
					onCompleted={async (result) => {
						if (result.type === "success") {
							showToast({
								title: i18n.t("completed"),
								message: i18n.t("delivery.package_bundle_created"),
							});
							await onCompletedOuter?.();
						}
						await closeDialog();
					}}
				/>
			),
		}));
	}
};
